import React from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  padding: 20px;
  margin: 20px auto;
  max-width: 600px;
  border: 2px solid #ff4d4f;
  border-radius: 12px;
  background: linear-gradient(135deg, #fff1f0, #ffebee);
  text-align: center;
  box-shadow: 0 4px 20px rgba(255, 77, 79, 0.1);
`;

const ErrorTitle = styled.h2`
  color: #ff4d4f;
  margin-bottom: 16px;
  font-family: 'Fredoka', Arial, sans-serif;
  font-size: 1.5rem;
`;

const ErrorDescription = styled.p`
  margin-bottom: 16px;
  color: #666;
  line-height: 1.5;
`;

const ErrorList = styled.ul`
  text-align: left;
  margin-bottom: 20px;
  color: #666;
  padding-left: 20px;
  
  li {
    margin-bottom: 8px;
  }
`;

const ErrorDetails = styled.div`
  margin-bottom: 20px;
  
  strong {
    color: #333;
  }
`;

const ErrorCode = styled.div`
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  margin-top: 8px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
  color: #d32f2f;
  text-align: left;
  word-break: break-word;
  max-height: 100px;
  overflow-y: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ErrorButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-family: 'Nunito', Arial, sans-serif;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &.primary {
    background: #ff4d4f;
    color: white;
    
    &:hover {
      background: #ff7875;
    }
  }
  
  &.secondary {
    background: #4ecdc4;
    color: white;
    
    &:hover {
      background: #45b7d1;
    }
  }
  
  &.tertiary {
    background: #f0f0f0;
    color: #333;
    
    &:hover {
      background: #e0e0e0;
    }
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    this.setState({ errorInfo });
    
    // Log to external service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: true
      });
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      const { fallback, showRetry = true, showHome = true } = this.props;

      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback(error, this.handleRetry);
      }

      return (
        <ErrorContainer>
          <ErrorTitle>Something went wrong</ErrorTitle>
          
          <ErrorDescription>
            We encountered an error while loading this part of the application. 
            This might be due to:
          </ErrorDescription>
          
          <ErrorList>
            <li>Network connectivity issues</li>
            <li>Temporary server problems</li>
            <li>Browser compatibility issues</li>
            <li>Data corruption or invalid state</li>
          </ErrorList>

          <ErrorDetails>
            <strong>What you can try:</strong>
            <ErrorList>
              <li>Refresh the page to reload the application</li>
              <li>Check your internet connection</li>
              <li>Clear your browser cache and cookies</li>
              <li>Try using a different browser</li>
              <li>Contact support if the problem persists</li>
            </ErrorList>
          </ErrorDetails>

          {error && (
            <ErrorDetails>
              <strong>Error details:</strong>
              <ErrorCode>
                {error.message || error.toString()}
                {showDetails && errorInfo && (
                  <>
                    <br /><br />
                    <strong>Stack trace:</strong><br />
                    {errorInfo.componentStack}
                  </>
                )}
              </ErrorCode>
              <ErrorButton 
                className="tertiary" 
                onClick={this.toggleDetails}
                style={{ marginTop: '8px', fontSize: '12px' }}
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </ErrorButton>
            </ErrorDetails>
          )}

          <ButtonGroup>
            <ErrorButton className="primary" onClick={this.handleRefresh}>
              Refresh Page
            </ErrorButton>
            
            {showRetry && (
              <ErrorButton className="secondary" onClick={this.handleRetry}>
                Try Again
              </ErrorButton>
            )}
            
            {showHome && (
              <ErrorButton className="tertiary" onClick={this.handleGoHome}>
                Go Home
              </ErrorButton>
            )}
          </ButtonGroup>

          <div style={{ 
            marginTop: '20px', 
            fontSize: '12px', 
            color: '#999',
            borderTop: '1px solid #eee',
            paddingTop: '15px'
          }}>
            If this problem persists, please contact support with the error details above.
            <br />
            Error ID: {Date.now().toString(36)}
          </div>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 